import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import type { RootState } from '../../store/store';
import { Avatar } from '../../components_catalyst/avatar';
import { getInitials, getAvatarColor } from '../../utils/avatarUtils';

/**
 * ActiveCollaborators Component
 *
 * Google Docs-style avatar group showing active collaborators:
 * - Overlapping avatar circles using Catalyst Avatar groups pattern
 * - Initials generated from userNames with consistent colors
 * - Connection status controls opacity (full when connected, faded when disconnected)
 * - Shows avatars for recent collaborators even when disconnected
 * - Overflow handling with "+N more" indicator for many collaborators
 * - Clean, minimal design without text labels
 * - Excludes current user's avatar (users see only other collaborators)
 */
export function ActiveCollaborators() {
  const { mapShortId } = useParams<{ mapShortId: string }>();

  // Get collaboration state from Redux
  const { activeCollaborators, connectionStatus, userProfile } = useSelector(
    (state: RootState) => state.map3Data,
  );

  // Get current map UUID and collaborators
  const currentMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.shortId === mapShortId),
  );

  const mapUuid = currentMap?.uuid;
  const allCollaborators = mapUuid ? activeCollaborators[mapUuid] || [] : [];

  // Get current user's uid for filtering (use Redux userProfile first, fallback to Firebase Auth)
  const currentUserUid = userProfile.uid;

  // Filter out current user from collaborators list (users should only see other people's avatars)
  const collaboratorsExcludingCurrentUser = allCollaborators.filter(
    (collaborator) => collaborator.uid !== currentUserUid,
  );

  // Filter out stale collaborators (haven't been seen in 90 seconds)
  // This is longer than the activity timeout (60s) to account for network delays
  const STALE_THRESHOLD_MS = 90 * 1000; // 90 seconds
  const now = Date.now();
  const collaborators = collaboratorsExcludingCurrentUser.filter((collaborator) => {
    if (!collaborator.lastSeen) return true; // Keep if no timestamp
    const lastSeenTime = new Date(collaborator.lastSeen).getTime();
    const timeSinceLastSeen = now - lastSeenTime;
    return timeSinceLastSeen < STALE_THRESHOLD_MS;
  });

  // Don't render if no active collaborators
  if (collaborators.length === 0) {
    return null;
  }

  // Connection status determines avatar opacity
  const isConnected = connectionStatus === 'connected';
  const avatarOpacity = isConnected ? 'opacity-100' : 'opacity-50';

  // Format last seen time for tooltip
  const formatLastSeen = (lastSeenISO: string | null): string => {
    if (!lastSeenISO) return 'Unknown';

    const lastSeen = new Date(lastSeenISO);
    const now = new Date();
    const diffMs = now.getTime() - lastSeen.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Handle overflow - show max 5 avatars plus overflow indicator
  const maxVisible = 5;
  const visibleCollaborators = collaborators.slice(0, maxVisible);
  const hiddenCount = Math.max(0, collaborators.length - maxVisible);

  return (
    <div className="flex items-center">
      {/* Avatar Group with overlapping layout */}
      <div
        className={`flex items-center -space-x-2 ${avatarOpacity} transition-opacity duration-200`}
      >
        {visibleCollaborators.map((collaborator, index) => {
          const userName = collaborator.userName || 'Anonymous User';
          const initials = getInitials(userName);
          const bgColor = getAvatarColor(userName, collaborator.uid);
          const tooltip = `${userName} • ${formatLastSeen(collaborator.lastSeen)}`;

          return (
            <div key={collaborator.uid} title={tooltip} className="relative group">
              <Avatar
                initials={initials}
                className={`size-8 ring-2 ring-white dark:ring-zinc-900 ${bgColor} text-white text-sm font-medium`}
                alt={`${userName} avatar`}
              />

              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                {tooltip}
              </div>
            </div>
          );
        })}

        {/* Overflow indicator */}
        {hiddenCount > 0 && (
          <div
            title={`${hiddenCount} more collaborator${hiddenCount > 1 ? 's' : ''}`}
            className="relative group"
          >
            <Avatar
              initials={`+${hiddenCount}`}
              className="size-8 ring-2 ring-white dark:ring-zinc-900 bg-zinc-500 text-white text-xs font-medium"
              alt={`${hiddenCount} more collaborators`}
            />

            {/* Tooltip on hover */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
              {hiddenCount} more collaborator{hiddenCount > 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>

      {/* Screen reader information */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {collaborators.length} active collaborator{collaborators.length > 1 ? 's' : ''}
        {isConnected ? '' : ' (connection interrupted)'}
      </div>
    </div>
  );
}
